import { Condition } from "../return-flow/condition";
import type { ConditionLeaf } from "./rules";
import {
  AiGuidanceLeafSchema,
  QuestionAnswerLeafSchema,
  readFlowConditionLeaf,
} from "./rules";
import type { Question } from "./snapshot-settings";

/** Facts a condition can read, grouped by where they come from. */
export const policyInputGroups = [
  "order",
  "fulfillment",
  "product",
  "customer",
  "return_items",
  "reasons",
  "method",
] as const;
export type PolicyInputGroup = (typeof policyInputGroups)[number];

/** One thing a condition reads: a group of facts, or the answer to one question. */
export type PolicyInput = { group: PolicyInputGroup } | { questionId: string };

export function policyInputKey(input: PolicyInput): string {
  return "group" in input
    ? `group:${input.group}`
    : `question:${input.questionId}`;
}

export function describePolicyInput(input: PolicyInput): string {
  return "group" in input
    ? `${input.group} inputs`
    : `the answer to question ${input.questionId}`;
}

/** Points where a setting can be decided, in order: today the shopper's portal steps, later processing too. Only validation reads this, to refuse a rule that could never be decided in time. */
const policyEvents = [
  "start",
  "select_items",
  "ask_before_reason_questions",
  "pick_reasons",
  "ask_after_reason_questions",
  "pick_method",
  "submit",
] as const;
export type PolicyEvent = (typeof policyEvents)[number];

export function policyEventIndex(event: PolicyEvent): number {
  return policyEvents.indexOf(event);
}

const inputGroupAvailableAfter: Record<PolicyInputGroup, PolicyEvent> = {
  order: "start",
  fulfillment: "start",
  product: "start",
  customer: "start",
  return_items: "select_items",
  reasons: "pick_reasons",
  method: "pick_method",
};

export function inputGroupKnownBefore(
  group: PolicyInputGroup,
  event: PolicyEvent,
): boolean {
  return (
    policyEventIndex(inputGroupAvailableAfter[group]) < policyEventIndex(event)
  );
}

export function questionAskedAt(phase: Question["phase"]): PolicyEvent {
  return phase === "before_reasons"
    ? "ask_before_reason_questions"
    : "ask_after_reason_questions";
}

/**
 * Every leaf is evaluated against one item's own `Parameters`. Order and
 * customer facts are the same on every item; the rest vary by item, including
 * `fulfillment`, which is loaded with the order but read from each item's own
 * fulfillment. `null` is never valid in a policy: support, email,
 * processing and registration-only conditions, and the flow-step
 * `multiple_choice` condition that `question_answer` replaces.
 */
const conditionInputGroup = {
  [Condition.COVERAGE]: "order",
  [Condition.PACKAGE_PROTECTION]: "order",
  [Condition.DISCOUNT]: "order",
  [Condition.ORDER_NUMBER]: "order",
  [Condition.ORDER_NOTE]: "order",
  [Condition.ORDER_DATE]: "order",
  [Condition.ORDER_WITHIN]: "order",
  [Condition.DELIVERY_WITHIN]: "fulfillment",
  [Condition.FULFILLMENT_WITHIN]: "fulfillment",
  [Condition.FULFILLMENT_TRACKING_UPDATE_WITHIN]: "fulfillment",
  [Condition.IS_DELIVERED]: "fulfillment",
  [Condition.ORDER_TAG]: "order",
  [Condition.SALES_CHANNEL]: "order",
  [Condition.ORDER_TIME_COLLECTION]: "order",
  [Condition.PAYMENT_METHOD]: "order",
  [Condition.PLATFORM]: "order",
  [Condition.ORDERLESS]: "order",
  [Condition.TRACKING_TYPE]: "order",
  [Condition.PURCHASE_CHANNEL]: "order",
  [Condition.LOCATION_NAME]: "fulfillment",
  [Condition.MERCHANT_CREATED]: "order",
  [Condition.CHANNEL]: "order",
  [Condition.EXTENDED_WARRANTY]: "order",
  [Condition.EXTENDED_WARRANTY_PURCHASED]: "order",
  [Condition.FINAL_SALE_RETURN]: "order",
  [Condition.LOYALTY_EARNED]: "order",
  [Condition.EXCHANGE_COUNT]: "order",
  [Condition.PRODUCT_PROPERTY]: "product",
  [Condition.PRODUCT_TAG]: "product",
  [Condition.PRODUCT_TYPE]: "product",
  [Condition.PRODUCT_VARIANT_METAFIELD]: "product",
  [Condition.PRODUCT_METAFIELD]: "product",
  [Condition.COLLECTION]: "product",
  [Condition.SKU]: "product",
  [Condition.VENDOR]: "product",
  [Condition.PRICE]: "product",
  [Condition.COMPARE_AT_PRICE]: "product",
  [Condition.ITEM_DISCOUNT]: "product",
  [Condition.RETURN_VALUE]: "product",
  [Condition.PRODUCT_AVAILABLE]: "product",
  [Condition.WITHDRAWAL_EXCLUSION]: "product",
  [Condition.CUSTOMER_TAG]: "customer",
  [Condition.CUSTOMER_EMAIL]: "customer",
  [Condition.CUSTOMER_COUNTRY]: "customer",
  [Condition.CUSTOMER_ADDRESS]: "customer",
  [Condition.NUM_CUSTOMER_CLAIMS]: "customer",
  [Condition.NUM_CUSTOMER_RETURNS]: "customer",
  [Condition.NUM_ORDERS]: "customer",
  [Condition.RETURN_RATE]: "customer",
  [Condition.ORDER_COUNT_GREATER_THAN]: "customer",
  [Condition.TOTAL_SPENT_GREATER_THAN]: "customer",
  [Condition.DROPOFF_NEARBY]: "customer",
  [Condition.CUSTOMER_EMAIL_SUBSCRIBER]: "customer",
  [Condition.CUSTOMER_SMS_SUBSCRIBER]: "customer",
  [Condition.ITEMS_BEING_RETURNED_COUNT]: "return_items",
  [Condition.NUM_ITEMS_IN_RETURN]: "return_items",
  [Condition.NON_RETURNED_VALUE]: "return_items",
  [Condition.TOTAL_RETURN_VALUE]: "return_items",
  [Condition.TOTAL_PRE_DISCOUNT_PRICE]: "return_items",
  [Condition.RETURN_TYPE]: "return_items",
  [Condition.IS_GIFT_RETURN]: "return_items",
  [Condition.IS_GREEN_RETURN]: "return_items",
  [Condition.RETURN_DATE]: "return_items",
  [Condition.IS_REPAIR]: "return_items",
  [Condition.REPLACEMENT_UNAVAILABLE]: "return_items",
  [Condition.RETURN_TOGETHER_ITEM_RETURNED_SEPARATELY]: "return_items",
  [Condition.RETURN_REASON]: "reasons",
  [Condition.DAMAGED]: "reasons",
  [Condition.WRONG_PRODUCT]: "reasons",
  [Condition.MULTIPLE_CHOICE]: null,
  [Condition.RETURN_METHOD]: "method",
  [Condition.COMPENSATION_METHOD]: "method",
  [Condition.IS_REFUND_RETURN]: "method",
  [Condition.ADJUSTMENT_AMOUNT]: "method",
  [Condition.AFTER_HOURS]: null,
  [Condition.WEEKLY_SCHEDULE]: null,
  [Condition.TICKET_CHANNEL]: null,
  [Condition.TOPIC]: null,
  [Condition.RECEIVED_AT_EMAIL]: null,
  [Condition.SENT_FROM_EMAIL]: null,
  [Condition.SUBJECT_LINE]: null,
  [Condition.MESSAGE_BODY_MATCHES]: null,
  [Condition.ASSIGNMENT_TYPE]: null,
  [Condition.PROCESSING_TYPE]: null,
  [Condition.PRODUCT_WRITE_IN]: null,
} as const satisfies Record<Condition["type"], PolicyInputGroup | null>;

const inputGroupByWireType = new Map<string, PolicyInputGroup | null>();
for (const value of Object.values(Condition)) {
  if (typeof value === "symbol" && value.description) {
    inputGroupByWireType.set(value.description, conditionInputGroup[value]);
  }
}

export type ParsedConditionLeaf =
  | { kind: "question"; input: { questionId: string }; optionIds: string[] }
  | { kind: "guidance"; input: { group: "reasons" }; guidanceId: string }
  | { kind: "flow"; input: { group: PolicyInputGroup }; condition: Condition }
  /** A support or email condition, which reads nothing a policy has. */
  | { kind: "unsupported" };
export type EvaluableConditionLeaf = Exclude<
  ParsedConditionLeaf,
  { kind: "unsupported" }
>;

const parsedLeavesByObject = new WeakMap<
  ConditionLeaf,
  ParsedConditionLeaf | undefined
>();

/** What a leaf reads and how to evaluate it, parsed once per leaf object. `undefined` for an unknown or malformed condition. */
export function parseConditionLeaf(
  leaf: ConditionLeaf,
): ParsedConditionLeaf | undefined {
  if (parsedLeavesByObject.has(leaf)) {
    return parsedLeavesByObject.get(leaf);
  }
  const parsedLeaf = parseConditionLeafUncached(leaf);
  parsedLeavesByObject.set(leaf, parsedLeaf);
  return parsedLeaf;
}

/** The default branch is the open set of flow condition wire names, so it cannot be exhaustive. */
function parseConditionLeafUncached(
  leaf: ConditionLeaf,
): ParsedConditionLeaf | undefined {
  switch (leaf.type) {
    case "question_answer": {
      const parsed = QuestionAnswerLeafSchema.safeParse(leaf);
      return parsed.success
        ? {
            kind: "question",
            input: { questionId: parsed.data.questionId },
            optionIds: parsed.data.optionIds,
          }
        : undefined;
    }
    case "ai_guidance_result": {
      const parsed = AiGuidanceLeafSchema.safeParse(leaf);
      return parsed.success
        ? {
            kind: "guidance",
            input: { group: "reasons" },
            guidanceId: parsed.data.guidanceId,
          }
        : undefined;
    }
    default:
      return parseFlowConditionLeaf(leaf);
  }
}

function parseFlowConditionLeaf(
  leaf: ConditionLeaf,
): ParsedConditionLeaf | undefined {
  const group = inputGroupByWireType.get(leaf.type);
  if (group === undefined) {
    return undefined;
  }
  if (group === null) {
    return { kind: "unsupported" };
  }
  try {
    return {
      kind: "flow",
      input: { group },
      condition: readFlowConditionLeaf(leaf),
    };
  } catch {
    return undefined;
  }
}
